import { useTranslation } from '@/i18n'
import { Switch } from '@/components/ui/switch'
import { IntuiBadge } from '@/components/intueri/IntuiBadge/IntuiBadge'
import type { Workflow } from './types'
import ExecutorAvatars from './ExecutorAvatars'
import WorkflowRowMenu, { type WorkflowRowActions } from './WorkflowRowMenu'

/**
 * Grid card for a single workflow (Figma "1. Workflow Entry Point" — grid).
 * Title + two-line description, executor avatars bottom-left, and the Active
 * toggle bottom-right (with the Draft pill beside it for unpublished drafts).
 */
export default function WorkflowCard({
  workflow,
  canManage = true,
  menuHidden = false,
  active = false,
  onToggleActive,
  ...actions
}: {
  workflow: Workflow
  canManage?: boolean
  /** Hide the row menu entirely (annotators — read-only everywhere). */
  menuHidden?: boolean
  /** The schedule toggle state (client-side settings until the scheduler lands). */
  active?: boolean
  onToggleActive?: (workflow: Workflow, active: boolean) => void
} & WorkflowRowActions) {
  const { t } = useTranslation()
  return (
    <div className="group relative flex h-full min-h-[168px] flex-col rounded-xl border border-border bg-background transition-colors hover:border-foreground/20 hover:bg-muted/40 dark:bg-dark-card">
      <button
        type="button"
        onClick={() => actions.onOpen?.(workflow)}
        className="flex w-full flex-1 flex-col gap-3 rounded-t-xl p-4 pb-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <h3 className="line-clamp-2 break-words pe-7 text-base font-semibold text-foreground" title={workflow.name}>
          {workflow.name}
        </h3>
        <p className="line-clamp-2 flex-1 text-sm text-muted-foreground">{workflow.description}</p>
      </button>
      {/* Footer row: executors left · Draft pill + Active toggle right. */}
      <div className="flex items-center justify-between gap-2 p-4 pt-3">
        <ExecutorAvatars executors={workflow.executors} size="sm" />
        <div className="flex items-center gap-2">
          {workflow.draft && (
            <IntuiBadge variant="secondary">
              {t('workflow:row.draft', { defaultValue: 'Draft' })}
            </IntuiBadge>
          )}
          {/* UI-44 (Pegah): "It's totally unclear what this toggle is for we
              need to somehow clarify." It carried only an aria-label — a screen
              reader knew what it was, a sighted user had a bare switch with no
              text anywhere near it. The table view never had this problem: its
              column header names it. The card had no equivalent, so it says it
              here, with the same word — "Schedule" (Putra 2026-09-16), which is
              what the toggle actually turns on. Card and list must not drift. */}
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            {t('workflow:row.schedule', { defaultValue: 'Schedule' })}
            <Switch
              checked={active}
              disabled={!canManage}
              onCheckedChange={(checked) => onToggleActive?.(workflow, checked)}
            />
          </label>
        </div>
      </div>
      <div className="absolute end-2 top-2 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <WorkflowRowMenu workflow={workflow} canManage={canManage} hidden={menuHidden} {...actions} />
      </div>
    </div>
  )
}
